import {
    Award,
    CalendarCheck,
    Gem,
    Globe2,
    MessageSquareReply,
    PenTool,
    ShieldCheck,
} from "lucide-react";
import type React from "react";
import type { LocalizationKey } from "@/main/util/LocalizationUtil";

/**
 * Family code → its catalog keys, written as LITERALS.
 *
 * AGENTS.md rule 11 bans casting a built string to `LocalizationKey`, and this is why the ban is worth
 * obeying rather than working around: because every key below is a literal, `LocalizationKey` resolves
 * them at compile time, so **adding a family to the backend register without adding its copy is a `tsc`
 * error** rather than a profile rendering the raw string "achievements.family.S1.name" at a user.
 *
 * That closes, for this feature, the standing gap rule 11 describes — a typo'd lookup key is invisible
 * to every other guard. The cost is one line per family, paid once.
 *
 * This table lives in its own module rather than beside the cabinet because the rail and the dialog
 * both resolve names from it (design §9.1). Two tables would be two chances to drift, and there is
 * deliberately no short-form name for the rail: a short form is another string per family per locale,
 * i.e. 504 more values and a second run at the defect class §14.8.1 documents.
 *
 * `detail` (design §9.1.3) is the sentence that says what actually COUNTS — the rule that stops a
 * reader concluding the counter is broken. It is a separate key rather than a rewrite of `behaviour`
 * on purpose: rewriting the English of a key already translated into 24 locales leaves 24 values that
 * are well-formed, parity-satisfying and now describe something slightly different, which no guard in
 * this repo can see. It is required on every family rather than optional, so a new family cannot ship
 * with the reading it invites left unaddressed.
 *
 * The backend deliberately does NOT mirror `detail`: only this card reads it, and 28 unread keys there
 * would be 700 values of translation debt for nothing (§14.9).
 */
export const FAMILY_COPY: Record<
    string,
    { name: LocalizationKey; behaviour: LocalizationKey; detail: LocalizationKey }
> = {
    R1: {
        name: "achievements.family.R1.name",
        behaviour: "achievements.family.R1.behaviour",
        detail: "achievements.family.R1.detail",
    },
    R2: {
        name: "achievements.family.R2.name",
        behaviour: "achievements.family.R2.behaviour",
        detail: "achievements.family.R2.detail",
    },
    R3: {
        name: "achievements.family.R3.name",
        behaviour: "achievements.family.R3.behaviour",
        detail: "achievements.family.R3.detail",
    },
    R4: {
        name: "achievements.family.R4.name",
        behaviour: "achievements.family.R4.behaviour",
        detail: "achievements.family.R4.detail",
    },
    R5: {
        name: "achievements.family.R5.name",
        behaviour: "achievements.family.R5.behaviour",
        detail: "achievements.family.R5.detail",
    },
    R6: {
        name: "achievements.family.R6.name",
        behaviour: "achievements.family.R6.behaviour",
        detail: "achievements.family.R6.detail",
    },
    R7: {
        name: "achievements.family.R7.name",
        behaviour: "achievements.family.R7.behaviour",
        detail: "achievements.family.R7.detail",
    },
    R8: {
        name: "achievements.family.R8.name",
        behaviour: "achievements.family.R8.behaviour",
        detail: "achievements.family.R8.detail",
    },
    R9: {
        name: "achievements.family.R9.name",
        behaviour: "achievements.family.R9.behaviour",
        detail: "achievements.family.R9.detail",
    },
    R10: {
        name: "achievements.family.R10.name",
        behaviour: "achievements.family.R10.behaviour",
        detail: "achievements.family.R10.detail",
    },
    R11: {
        name: "achievements.family.R11.name",
        behaviour: "achievements.family.R11.behaviour",
        detail: "achievements.family.R11.detail",
    },
    R12: {
        name: "achievements.family.R12.name",
        behaviour: "achievements.family.R12.behaviour",
        detail: "achievements.family.R12.detail",
    },
    P1: {
        name: "achievements.family.P1.name",
        behaviour: "achievements.family.P1.behaviour",
        detail: "achievements.family.P1.detail",
    },
    P2: {
        name: "achievements.family.P2.name",
        behaviour: "achievements.family.P2.behaviour",
        detail: "achievements.family.P2.detail",
    },
    P3: {
        name: "achievements.family.P3.name",
        behaviour: "achievements.family.P3.behaviour",
        detail: "achievements.family.P3.detail",
    },
    P4: {
        name: "achievements.family.P4.name",
        behaviour: "achievements.family.P4.behaviour",
        detail: "achievements.family.P4.detail",
    },
    P5: {
        name: "achievements.family.P5.name",
        behaviour: "achievements.family.P5.behaviour",
        detail: "achievements.family.P5.detail",
    },
    S1: {
        name: "achievements.family.S1.name",
        behaviour: "achievements.family.S1.behaviour",
        detail: "achievements.family.S1.detail",
    },
    S2: {
        name: "achievements.family.S2.name",
        behaviour: "achievements.family.S2.behaviour",
        detail: "achievements.family.S2.detail",
    },
    S3: {
        name: "achievements.family.S3.name",
        behaviour: "achievements.family.S3.behaviour",
        detail: "achievements.family.S3.detail",
    },
    S8: {
        name: "achievements.family.S8.name",
        behaviour: "achievements.family.S8.behaviour",
        detail: "achievements.family.S8.detail",
    },
    C1: {
        name: "achievements.family.C1.name",
        behaviour: "achievements.family.C1.behaviour",
        detail: "achievements.family.C1.detail",
    },
    C2: {
        name: "achievements.family.C2.name",
        behaviour: "achievements.family.C2.behaviour",
        detail: "achievements.family.C2.detail",
    },
    C3: {
        name: "achievements.family.C3.name",
        behaviour: "achievements.family.C3.behaviour",
        detail: "achievements.family.C3.detail",
    },
    C4: {
        name: "achievements.family.C4.name",
        behaviour: "achievements.family.C4.behaviour",
        detail: "achievements.family.C4.detail",
    },
    Rg1: {
        name: "achievements.family.Rg1.name",
        behaviour: "achievements.family.Rg1.behaviour",
        detail: "achievements.family.Rg1.detail",
    },
    Rg5: {
        name: "achievements.family.Rg5.name",
        behaviour: "achievements.family.Rg5.behaviour",
        detail: "achievements.family.Rg5.detail",
    },
    X1: {
        name: "achievements.family.X1.name",
        behaviour: "achievements.family.X1.behaviour",
        detail: "achievements.family.X1.detail",
    },
    X2: {
        name: "achievements.family.X2.name",
        behaviour: "achievements.family.X2.behaviour",
        detail: "achievements.family.X2.detail",
    },
    X3: {
        name: "achievements.family.X3.name",
        behaviour: "achievements.family.X3.behaviour",
        detail: "achievements.family.X3.detail",
    },
    X4: {
        name: "achievements.family.X4.name",
        behaviour: "achievements.family.X4.behaviour",
        detail: "achievements.family.X4.detail",
    },
    X7: {
        name: "achievements.family.X7.name",
        behaviour: "achievements.family.X7.behaviour",
        detail: "achievements.family.X7.detail",
    },
};

export const LAYER_COPY: Record<string, LocalizationKey> = {
    RESPONSE: "achievements.layer.RESPONSE",
    CRAFT: "achievements.layer.CRAFT",
    PERSISTENCE: "achievements.layer.PERSISTENCE",
    STEWARDSHIP: "achievements.layer.STEWARDSHIP",
    RANGE: "achievements.layer.RANGE",
    RARE: "achievements.layer.RARE",
};

/**
 * One icon per LAYER — never per family (design §9.1.1).
 *
 * Per-family icons would be 21 taste calls today and one more forever, none of them checkable, and an
 * icon that does not obviously mean "Cold case" costs the viewer more than no icon at all. Six are
 * learnable, and they let two chips from the same layer read as siblings — which is the only grouping
 * the rail gives up by not being grouped by layer.
 */
export const LAYER_ICON: Record<string, React.ElementType> = {
    RESPONSE: MessageSquareReply,
    CRAFT: PenTool,
    PERSISTENCE: CalendarCheck,
    STEWARDSHIP: ShieldCheck,
    RANGE: Globe2,
    RARE: Gem,
};

/**
 * A layer the backend registered before the frontend learned about it degrades to a neutral medal
 * rather than to a gap, so one unknown layer cannot make the whole row ragged.
 */
export const FALLBACK_LAYER_ICON: React.ElementType = Award;

export function layerIconFor(layer: string): React.ElementType {
    return LAYER_ICON[layer] ?? FALLBACK_LAYER_ICON;
}

/** Rung numerals I–VIII (decision §15.1). Index 0 is unused — tiers are 1-based. */
export const RUNG_COPY: LocalizationKey[] = [
    "achievements.rung.1",
    "achievements.rung.1",
    "achievements.rung.2",
    "achievements.rung.3",
    "achievements.rung.4",
    "achievements.rung.5",
    "achievements.rung.6",
    "achievements.rung.7",
    "achievements.rung.8",
];

/**
 * The numeral for a tier, or `null` past the end of the scale.
 *
 * Returning `null` rather than clamping matters: a backend that mints a tier IX would otherwise render
 * as a second VIII, which is a silently wrong claim about someone's standing rather than a visible gap.
 */
export function rungCopyFor(tier: number): LocalizationKey | null {
    return RUNG_COPY[tier] ?? null;
}
